import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { subscriptionApi } from '../services/api';

const PaymentCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');
  const [checking, setChecking] = useState(true);

  // Poll for subscription status updates
  const { data: subscription, isLoading } = useQuery({
    queryKey: ['currentSubscription'],
    queryFn: () => subscriptionApi.getCurrent().then((r) => r.data),
    refetchInterval: checking ? 2000 : false, // Poll every 2 seconds while checking
    refetchIntervalInBackground: true,
  });

  useEffect(() => {
    // Stop checking after subscription is confirmed or after 30 seconds
    const timeout = setTimeout(() => setChecking(false), 30000);

    if (subscription?.has_subscription && subscription?.subscription_status === 'active') {
      setChecking(false);
    }

    return () => clearTimeout(timeout);
  }, [subscription]);

  const isSuccess = status === 'completed' || subscription?.subscription_status === 'active';
  const isPending = checking && !isSuccess;

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center max-w-md">
        {isPending ? (
          <>
            <div className="mx-auto w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mb-6" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Processing Payment
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              Please wait while we confirm your payment...
            </p>
          </>
        ) : isSuccess ? (
          <>
            <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6">
              <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Payment Successful!
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              Your subscription is now active. Thank you for subscribing!
            </p>
            {subscription?.tier && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">Your Plan</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {subscription.tier.name}
                </p>
              </div>
            )}
            <div className="space-x-4">
              <Link to="/" className="btn btn-primary">
                Start Writing
              </Link>
              <Link to="/subscription" className="btn btn-secondary">
                View Subscription
              </Link>
            </div>
          </>
        ) : (
          <>
            <div className="mx-auto w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mb-6">
              <svg className="w-8 h-8 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Payment Pending
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              Your payment is being processed. This may take a moment.
              You'll receive an email confirmation once complete.
            </p>
            <div className="space-x-4">
              <Link to="/subscription" className="btn btn-primary">
                Check Status
              </Link>
              <Link to="/" className="btn btn-secondary">
                Return Home
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentCallbackPage;
